import { TextFieldElement, UseFormReturn } from '@graphcommerce/ecommerce-ui'
import { ConfigurableProduct, CustomizableFieldOption } from '@graphcommerce/graphql-mesh'
import { useFormAddProductsToCart } from '@graphcommerce/magento-product/components/AddProductsToCart'
import { ProductCustomizableFragment } from '@graphcommerce/magento-product/components/ProductCustomizable//ProductCustomizable.gql'
import { RenderType, TypeRenderer, filterNonNullableKeys } from '@graphcommerce/next-ui'
import { Tooltip } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'

export type OptionTypeRenderer = TypeRenderer<
  NonNullable<NonNullable<ProductCustomizableFragment['options']>[number]> & {
    optionIndex: number
    index: number
  }
>

const CustomizableAreaOption = React.memo<
  React.ComponentProps<OptionTypeRenderer['CustomizableAreaOption']>
>((props) => {
  const { uid, areaValue, required, optionIndex, index, title } = props
  const maxLength = areaValue?.max_characters ?? undefined
  const { control, register } = useFormAddProductsToCart()

  return (
    <>
      <input
        type='hidden'
        {...register(`cartItems.${index}.entered_options.${optionIndex}.uid`)}
        value={uid}
      />
      <TextFieldElement
        color='primary'
        multiline
        minRows={3}
        control={control}
        name={`cartItems.${index}.entered_options.${optionIndex}.value`}
        label={title}
        required={Boolean(required)}
        validation={{ maxLength }}
        helperText={(maxLength ?? 0) > 0 && `A maximum of ${maxLength}`}
      />
    </>
  )
})

const CustomizableDropDownOption = React.memo<
  React.ComponentProps<OptionTypeRenderer['CustomizableDropDownOption']>
>((props) => {
  const { uid, required, optionIndex, index, title, dropdownValue } = props
  const { control, register } = useFormAddProductsToCart()
  const fieldName = `cartItems.${index}.entered_options.${optionIndex}.value` as const
  const options = filterNonNullableKeys(dropdownValue, ['title'])
  const isThreeColTrigger = options.length > 12 || (title?.toLowerCase().includes('size') ?? false)

  return (
    <>
      <input
        type='hidden'
        {...register(`cartItems.${index}.entered_options.${optionIndex}.uid`)}
        value={uid}
      />
      <Controller
        control={control}
        name={fieldName as any}
        defaultValue={options[0]?.uid ?? ''}
        rules={{ required: Boolean(required) }}
        render={({ field }) => (
          <div className='my-3.75'>
            <p className='mb-3 text-[14px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
              {title}
            </p>
            <div className={isThreeColTrigger ? 'grid grid-cols-1 gap-2 lg:grid-cols-3' : 'grid grid-cols-1 gap-2 lg:grid-cols-2'}>
              {options.map((option) => {
                const isSelected = field.value === option.uid
                return (
                  <button
                    key={option.uid}
                    type='button'
                    onClick={() => field.onChange(option.uid)}
                    className={`w-full overflow-hidden rounded-[4px] px-2.5 py-2.5 transition-all active:scale-[0.97] ${
                      isSelected
                        ? 'bg-[#D1CBCB33] text-black border-[1.6px] border-[#5D54DE] shadow-[0px_2px_3px_0px_rgba(0,0,0,0.25)]'
                        : 'bg-[#F8F8F8] text-black border-[1.4px] border-[#908B8B]'
                    }`}
                  >
                    <span className={`block truncate text-[13px] uppercase tracking-[0.04em] ${isSelected ? 'font-bold' : 'font-medium'}`}>
                      {option.title}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}
      />
    </>
  )
})

const CustomizableFieldOptionRenderer = React.memo<
  React.ComponentProps<OptionTypeRenderer['CustomizableFieldOption']> & {
    form: UseFormReturn<any>
    selectedVariantSku?: string | null
  }
>((props) => {
  const { uid, title, form, selectedVariantSku, index, required } = props
  const { simples, color_list } = props as CustomizableFieldOption
  const { setValue, watch } = form

  const shouldBeRequired = selectedVariantSku && simples?.includes(selectedVariantSku) && required

  useEffect(() => {
    if (uid) {
      form.register(`enteredOptions.${index}`, {
        required: shouldBeRequired || false,
        value: {
          uid,
          value: color_list?.find((color) => color && color.default)
            ? color_list?.find((color) => color && color.default)?.code
            : '',
        },
        validate: ({ value }) => {
          if (shouldBeRequired && !value) {
            return false
          }
          return true
        },
      })
    }
  }, [form, color_list, shouldBeRequired, index, uid, title])

  useEffect(
    () => () => {
      form.unregister(`enteredOptions.${index}`)
    },
    [title, form.unregister, index],
  )

  const enteredOptions = watch('enteredOptions')

  const handleColorSelect = useCallback(
    (newValue: string) => {
      const entered = Array.isArray(enteredOptions)
        ? [...enteredOptions].filter((item) => item)
        : []
      if (entered.find((o) => o.uid === uid)) {
        entered.find((o) => o.uid === uid).value = newValue
      } else {
        entered.push({ uid, value: newValue })
      }
      setValue(`enteredOptions`, entered, {})
    },
    [setValue, uid, enteredOptions],
  )

  const selectedColorValue = enteredOptions?.find((o) => o?.uid === uid)?.value ?? ''

  const [selectedLabel, setSelectedLabel] = useState<string>(() => {
    const defaultColor = color_list?.find((c) => c?.default) ?? color_list?.[0]
    return defaultColor?.label ?? ''
  })

  const selectedColorLabel = color_list?.find(
    (c) => c?.code === selectedColorValue && (!selectedLabel || c?.label === selectedLabel),
  )?.label

  return (
    <div className='my-3.75'>
      <p className='mb-3 text-[14px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
        {title}
        {selectedColorLabel && (
          <span className='font-medium text-black'>{` = ${selectedColorLabel.toUpperCase()}`}</span>
        )}
      </p>
      <div className='grid grid-cols-4 gap-x-2 gap-y-5'>
        {color_list?.filter(Boolean).map((color) => {
          const isSelected =
            selectedColorValue === color!.code &&
            (!selectedLabel || selectedLabel === (color!.label ?? ''))
          return (
            <Tooltip key={color!.code} title={color!.label ?? ''} placement='top' arrow>
              <div className='relative'>
                <button
                  type='button'
                  aria-label={color!.label ?? ''}
                  onClick={() => {
                    if (uid && color!.code) {
                      handleColorSelect(color!.code)
                      setSelectedLabel(color!.label ?? '')
                    }
                  }}
                  className={`h-9 w-full rounded-[6px] transition-all active:scale-[0.95] ${
                    isSelected
                      ? 'ring-2 ring-[#5D54DE] ring-offset-2'
                      : 'ring-1 ring-[#d9d9d9] hover:ring-[#afafaf]'
                  }`}
                  style={{ backgroundColor: color!.code ?? '#ccc' }}
                />
                {isSelected && (
                  <span className='absolute left-1/2 top-full -translate-x-1/2 pt-0.5 whitespace-nowrap text-center text-[9px] font-medium leading-tight text-black'>
                    {color!.label}
                  </span>
                )}
              </div>
            </Tooltip>
          )
        })}
      </div>
    </div>
  )
})

const defaultRenderer = {
  CustomizableAreaOption,
  CustomizableCheckboxOption: () => <div>checkbox not implemented</div>,
  CustomizableDateOption: () => <div>date not implemented</div>,
  CustomizableDropDownOption,
  CustomizableFieldOption: CustomizableFieldOptionRenderer,
  CustomizableFileOption: () => <div>file not implemented</div>,
  CustomizableMultipleOption: () => <div>multi not implemented</div>,
  CustomizableRadioOption: () => <div>radios not implemented</div>,
}

type Simplify<T> = { [KeyType in keyof T]: T[KeyType] }

type MissingOptionTypeRenderer = Omit<OptionTypeRenderer, keyof typeof defaultRenderer>
type DefinedOptionTypeRenderer = Partial<Pick<OptionTypeRenderer, keyof typeof defaultRenderer>>

type OptionTypeRendererProp = Simplify<
  keyof MissingOptionTypeRenderer extends never
    ? (MissingOptionTypeRenderer & DefinedOptionTypeRenderer) | undefined
    : MissingOptionTypeRenderer & DefinedOptionTypeRenderer
>

type ProductCustomizableProps = {
  product: Pick<ConfigurableProduct, 'options'> & ProductCustomizableFragment
  index?: number
  form: UseFormReturn<any>
  selectedVariantSku?: string | null
} & (keyof MissingOptionTypeRenderer extends never
  ? { renderer?: OptionTypeRendererProp }
  : { renderer: OptionTypeRendererProp })

export function ProductCustomizable(props: ProductCustomizableProps) {
  const { product, renderer, index = 0, form, selectedVariantSku } = props

  const filterOptionsBySelectedVariant = useCallback(
    (option) => {
      if ('color_list' in option) {
        const { simples } = option as CustomizableFieldOption
        if (Array.isArray(simples) && selectedVariantSku && !simples.includes(selectedVariantSku)) {
          return false
        }
      }
      return true
    },
    [selectedVariantSku],
  )

  return (
    <>
      {filterNonNullableKeys(product.options, ['sort_order'])
        .filter(filterOptionsBySelectedVariant)
        .map((option, oIndex) => (
          <RenderType
            key={option.uid}
            renderer={{ ...defaultRenderer, ...renderer }}
            {...option}
            form={form}
            selectedVariantSku={selectedVariantSku}
            optionIndex={option.sort_order + 100}
            index={oIndex}
          />
        ))}
    </>
  )
}
